import { Button, Card, Empty, Skeleton } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { ContentCustom, PageHeaderCustom } from '../../../components';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useListContent } from './listContent';
import { useErrorMessage } from '../../../utils/errorMessage';
import { ImagesLayout } from '../supervisions/ImagesLayout';
import { useSocketContext } from '../../../contexts/SocketContext';
import { profileLockMessage } from '../utils/profileLockMessage';
import { editingLocked } from '../../../utils/editingLocked';
import { CardTitle } from '../../../components/CardTitle/CardTitle';
import { InfoGrid } from '../../../components/InfoGrid/InfoGrid';

const StyledSections = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(420px, 100%), 1fr));
  gap: 24px;
  align-items: start;
`;

// Icône de chaque section, dans l'ordre de `additionalInfos`
const SECTION_ICONS = [
  'environment',
  'skin',
  'clear',
  'coffee',
  'sun',
  'car',
  'tool'
];

const toGridItems = (items) =>
  (items || []).map(({ title, content }) => ({ label: title, content }));

/**
 * Renders the component for displaying the pet sitting address details.
 * @component
 * @returns {JSX.Element} The JSX element representing the ShowPetSittingAddress component.
 */
export const ShowPetSittingAddress = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, dispatchAPI } = useAuthContext();
  const { message } = useErrorMessage();
  const { profileLock } = useSocketContext();
  const { id } = useParams();

  const [owner, setOwner] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const getOwner = async () => {
    try {
      setIsLoading(true);
      const { data } = await dispatchAPI('GET', {
        url: `/owners?email=${user?.email}&populate=petsitting_address`
      });
      setOwner(data?.[0]);
    } catch (e) {
      message(e);
    } finally {
      // A failed call must not leave the skeleton on until the next reload.
      setIsLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      await getOwner();
    })();
  }, []);

  const { petsitting_address } = owner || {};

  const titles = [
    // Information générales
    t('petsittingaddress.card_title.general_informations'),
    // Chambre et linge
    t('petsittingaddress.card_title.room_and_laundry'),
    // Salle de bain
    t('petsittingaddress.card_title.bathroom'),
    // Cuisine
    t('petsittingaddress.card_title.kitchen'),
    // Exterieur
    t('petsittingaddress.card_title.outside'),
    // Parking et installation
    t('petsittingaddress.card_title.parking_and_installation'),
    // Equipement
    t('petsittingaddress.card_title.equipment')
  ];

  const { mainInfos, additionalInfos } = useListContent(petsitting_address);
  const notPetsittingAddressExist = Object.keys(
    petsitting_address || []
  ).includes('address');

  const goToForm = () => {
    if (editingLocked(profileLock, id)) {
      profileLockMessage(t, 'profile');
      return;
    }
    navigate(
      `/petsitting-address/${!notPetsittingAddressExist ? 'create' : 'edit'}/${
        petsitting_address?._id || owner?._id
      }`
    );
  };

  return (
    <>
      <PageHeaderCustom
        withoutBreadCrumb
        title={t('owners.users_shows.title.petsitting_address_details')}
        extra={
          isLoading ? (
            <Skeleton.Button active loading={isLoading} block />
          ) : (
            <Button
              type="primary"
              icon={
                notPetsittingAddressExist ? <EditOutlined /> : <PlusOutlined />
              }
              onClick={goToForm}
            >
              {!notPetsittingAddressExist
                ? t('petsittingaddress.form.title.create')
                : t('petsittingaddress.form.title.edit')}
            </Button>
          )
        }
      />
      <ContentCustom transparent style={{ gap: 24 }}>
        {!isLoading && !notPetsittingAddressExist ? (
          <Card>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t(
                'owners.users_shows.messages.no_petsitting_address'
              )}
              style={{ padding: '32px 0' }}
            >
              <Button type="primary" icon={<PlusOutlined />} onClick={goToForm}>
                {t('petsittingaddress.form.title.create')}
              </Button>
            </Empty>
          </Card>
        ) : (
          <>
            <Card
              title={
                <CardTitle icon="home">
                  {t('users.form.principal_infos')}
                </CardTitle>
              }
            >
              <Skeleton loading={isLoading} active>
                {petsitting_address?.dwelling_photos?.length > 0 && (
                  <div style={{ marginBottom: 24 }}>
                    <ImagesLayout
                      isLoading={isLoading}
                      url={petsitting_address.dwelling_photos}
                    />
                  </div>
                )}
                <InfoGrid items={toGridItems(mainInfos)} />
              </Skeleton>
            </Card>
            <StyledSections>
              {additionalInfos.map((info, index) => (
                <Card
                  key={titles[index]}
                  title={
                    <CardTitle icon={SECTION_ICONS[index]}>
                      {titles[index]}
                    </CardTitle>
                  }
                >
                  <Skeleton loading={isLoading} active>
                    <InfoGrid items={toGridItems(info)} />
                  </Skeleton>
                </Card>
              ))}
            </StyledSections>
          </>
        )}
      </ContentCustom>
    </>
  );
};
